/**
 * Certificados de curso completado (Space).
 *
 * La EMISIÓN es server-authoritative: al completar la última lección aprobada,
 * el write-path comprueba el criterio de completitud y sella un certificado
 * (snapshot de alumno/curso/versión/academia) con un id UUID verificable. El
 * cliente lo recibe por el stream privado `my_certificates` y lo LEE offline
 * (nunca lo escribe). Este módulo aporta:
 *   - el ESPEJO puro del criterio de completitud (para la UI local-first);
 *   - la plantilla de CONTENIDO del certificado (HTML imprimible → PDF del SO);
 *   - el cliente REST de la verificación pública (guest-first, sin cuenta).
 */

import { and, certificate, eq, isNull, type PowerSyncDrizzleDB } from "@space/powersync";
import { gradingScheme } from "../academy/grading/registry";
import { LESSON_PASS_THRESHOLD } from "./progress";

/** Fila mínima de lección para el criterio (subset de `lesson`). */
export interface CertificateLessonRow {
  id: string;
  deleted_at?: string | null;
}

/** Fila mínima de progreso para el criterio (subset de `lesson_progress`). */
export interface CertificateProgressRow {
  lesson_id: string | null;
  status: string | null;
  score: number | null;
}

/**
 * Criterio de COMPLETITUD del curso (espejo puro del servidor):
 * el curso tiene ≥ 1 lección viva y TODAS están `completed` con nota aprobada
 * (score >= LESSON_PASS_THRESHOLD — la autoritativa que re-corrige el server).
 * Un curso sin lecciones NUNCA está completo (no se certifica el vacío).
 */
export function isCourseCompleted(
  lessons: readonly CertificateLessonRow[],
  progress: readonly CertificateProgressRow[],
): boolean {
  const live = lessons.filter((l) => l.deleted_at == null);
  if (live.length === 0) {
    return false;
  }
  const passed = new Set(
    progress
      .filter((p) => p.status === "completed" && (p.score ?? 0) >= LESSON_PASS_THRESHOLD)
      .map((p) => p.lesson_id),
  );
  return live.every((l) => passed.has(l.id));
}

/** Datos mínimos del certificado que consume la plantilla. */
export interface CertificateData {
  id: string;
  learner_name?: string | null;
  course_title?: string | null;
  course_version?: number | null;
  org_name?: string | null;
  issued_at?: string | null;
  /** F3-B: nota final SELLADA al cierre de cohorte (valor nativo de la escala). */
  final_grade?: number | null;
  final_grade_scheme?: string | null;
  /**
   * Grupo E: cadena ya renderizada de la nota (snapshot). Presente en cierres
   * nuevos; permite mostrar correctamente certificados de MODELOS
   * PERSONALIZADOS (cuya `final_grade_scheme` es un id de modelo, no una clave
   * legada) aunque el modelo se edite/borre después.
   */
  final_grade_display?: string | null;
}

/** Contenido renderizable de la plantilla del certificado. */
export interface CertificateContent {
  certId: string;
  learnerName: string;
  courseTitle: string;
  /** Sello de versión del curso al emitir (auditoría de autoría). */
  versionLabel: string;
  /** Academia emisora, o `null` si el curso no se publicó vía organización. */
  orgName: string | null;
  /** Fecha de emisión formateada para el locale dado. */
  issuedAtLabel: string;
  /** URL pública de verificación (página guest-first `/verify/:id`). */
  verifyUrl: string;
  /**
   * Nota final sellada, renderizada con su escala (p. ej. "18 / 20"), o `null`
   * si el certificado se emitió sin cohorte evaluada (self-paced).
   */
  finalGradeLabel: string | null;
}

/**
 * PLANTILLA de contenido del certificado (pura y testeable): normaliza el
 * snapshot sincronizado a los campos que pinta la vista imprimible y la página
 * de verificación. `verifyBaseUrl` = origen del front (p. ej. window.origin).
 */
export function buildCertificateContent(
  cert: CertificateData,
  opts: { verifyBaseUrl: string; locale?: string },
): CertificateContent {
  const issuedAt = cert.issued_at ? new Date(cert.issued_at) : null;
  const issuedAtLabel =
    issuedAt && !Number.isNaN(issuedAt.getTime())
      ? new Intl.DateTimeFormat(opts.locale ?? "en", { dateStyle: "long" }).format(issuedAt)
      : "";
  // F3-B/grupo E: nota final sellada. Preferimos el SNAPSHOT ya renderizado
  // (`final_grade_display`, sellado al cierre — correcto también para modelos
  // personalizados); si falta (certificados previos) caemos a renderizar por la
  // escala legada sobre el valor NATIVO persistido en `certificate.final_grade`.
  const snapshot = cert.final_grade_display?.trim();
  const finalGradeLabel = snapshot
    ? snapshot
    : cert.final_grade != null
      ? gradingScheme(cert.final_grade_scheme).displayValue(cert.final_grade)
      : null;
  return {
    certId: cert.id,
    learnerName: cert.learner_name ?? "",
    courseTitle: cert.course_title ?? "",
    versionLabel: `v${cert.course_version ?? 1}`,
    orgName: cert.org_name ?? null,
    issuedAtLabel,
    verifyUrl: `${opts.verifyBaseUrl.replace(/\/$/, "")}/verify/${encodeURIComponent(cert.id)}`,
    finalGradeLabel,
  };
}

// ─── Lecturas local-first (PowerSync) ────────────────────────────────────────

/** Mis certificados (privados, sincronizados), el más reciente primero. */
export function getMyCertificatesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(certificate)
    .where(and(eq(certificate.user_id, userId), isNull(certificate.revoked_at)));
}

/** Certificado del usuario para UN curso (fila única, si ya fue emitido). */
export function getCourseCertificateQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select()
    .from(certificate)
    .where(
      and(
        eq(certificate.user_id, userId),
        eq(certificate.course_id, courseId),
        isNull(certificate.revoked_at),
      ),
    )
    .limit(1);
}

/** Un certificado propio por id (para la vista imprimible). */
export function getCertificateByIdQuery(db: PowerSyncDrizzleDB, certId: string) {
  return db.select().from(certificate).where(eq(certificate.id, certId)).limit(1);
}

// ─── Verificación pública (REST, guest-first) ────────────────────────────────

/** DTO camelCase de `models/course.rs::Certificate` (se mantiene a mano). */
export interface CertificateDTO {
  id: string;
  learnerName: string;
  courseTitle: string;
  courseVersion: number;
  orgName: string | null;
  issuedAt: string;
}

/** DTO de `models/course.rs::CertificateVerification`. */
export interface CertificateVerificationDTO {
  valid: boolean;
  certificate: CertificateDTO | null;
}

/**
 * Verifica un certificado por id contra el servidor (público, sin cuenta).
 * `valid=false` cubre tanto "no existe" como "revocado" — sin filtrar nada más.
 */
export async function fetchCertificateVerification(
  baseUrl: string,
  certId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CertificateVerificationDTO> {
  const res = await fetch(
    `${baseUrl.replace(/\/$/, "")}/api/certificates/${encodeURIComponent(certId)}/verify`,
    { signal: opts.signal },
  );
  if (!res.ok) {
    throw new Error(`certificate verification failed: ${res.status}`);
  }
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed DTO
  return (await res.json()) as CertificateVerificationDTO;
}
